import { cloneElement } from 'react';
import { useState, useEffect, useCallback } from 'react';
import { Box } from '@mui/system';

export default function MultiStepForm({ steps, onSubmit, onCancel }) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [data, setData] = useState({});
  const [users, setUsers] = useState({});
  const [direction, setDirection] = useState(0);
  const [isCancelling, setIsCancelling] = useState(false);

  useEffect(() => {
    if (isCancelling) {
      setIsCancelling(false);
      if (onCancel) {
        onCancel();
      }
    }
  }, [isCancelling, onCancel]);

  function handleCancel() {
    setDirection(-1);
    setIsCancelling(true);
  }

  const resetToFirstStep = useCallback(() => {
    setDirection(-1);
    setCurrentStepIndex(0);
    setData({});
    setUsers({});
  }, []);

  function next(result) {
    setDirection(1);
    let currentUsers = users;

    if (result?.users) {
      currentUsers = result.users;
      setUsers(currentUsers);
      const { users: _, ...otherData } = result;
      result = otherData;
    }
    const updatedData = { ...data, ...result };
    setData(updatedData);

    if (currentStepIndex === steps.length - 1) {
      if (onSubmit) {
        return onSubmit(updatedData, currentUsers);
      }
    }

    // Move to next step
    setCurrentStepIndex((i) => {
      if (i >= steps.length - 1) return i;
      return i + 1;
    });
  }

  function back() {
    setDirection(-1);
    setCurrentStepIndex((i) => {
      if (i <= 0) return i;
      return i - 1;
    });
  }

  const variants = {
    enter: (direction) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction) => ({
      x: direction < 0 ? 300 : -300,
      opacity: 0,
    }),
  };

  const createStepElement = (index) => {
    return cloneElement(steps[index], {
      next,
      back,
      data,
      users,
      resetToFirstStep,
      isLastStep: index === steps.length - 1,
      isFirstStep: index === 0,
      handleCancel: handleCancel,
    });
  };

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      <Box
        key={currentStepIndex}
        custom={direction}
        variants={variants}
        initial="enter"
        animate="center"
        exit="exit"
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
        }}
      >
        {createStepElement(currentStepIndex)}
      </Box>
    </Box>
  );
}
